import { useTranslation } from "react-i18next";
import { cn } from "@core/utils";

interface SkeletonProps {
  /** Size and shape come from the caller: `h-4 w-32`, `size-10 rounded-full`, … */
  className?: string;
}

/**
 * One placeholder block. Decorative: hidden from assistive tech, the group wrappers below carry
 * the single « Chargement… » announcement. The pulse stops under `prefers-reduced-motion`.
 */
export function Skeleton({ className }: SkeletonProps) {
  return (
    <div
      aria-hidden
      className={cn(
        "animate-pulse rounded-md bg-ink-100 motion-reduce:animate-none",
        className
      )}
    />
  );
}

interface SkeletonTextProps {
  lines?: number;
  className?: string;
}

/** A paragraph while it loads; the last line is shorter, as text is. */
export function SkeletonText({ lines = 3, className }: SkeletonTextProps) {
  const { t } = useTranslation("common");

  return (
    <div role="status" className={cn("flex flex-col gap-xs", className)}>
      <span className="sr-only">{t("loading")}</span>
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton
          key={index}
          className={cn(
            "h-3.5",
            index === lines - 1 && lines > 1 ? "w-3/5" : "w-full"
          )}
        />
      ))}
    </div>
  );
}

interface SkeletonRowsProps {
  rows?: number;
  /** Show a round avatar placeholder at the start of each row (team list, invitations). */
  withAvatar?: boolean;
  className?: string;
}

/** A bordered list while it loads: team members, invitations, cohorts. */
export function SkeletonRows({
  rows = 4,
  withAvatar = false,
  className,
}: SkeletonRowsProps) {
  const { t } = useTranslation("common");

  return (
    <div role="status" className={cn("flex flex-col gap-xs", className)}>
      <span className="sr-only">{t("loading")}</span>
      {Array.from({ length: rows }, (_, index) => (
        <div
          key={index}
          className="flex items-center gap-sm rounded-lg border border-(--border-default) bg-surface-card p-sm"
        >
          {withAvatar ? (
            <Skeleton className="size-8 shrink-0 rounded-full" />
          ) : null}
          <div className="flex flex-1 flex-col gap-xxs">
            <Skeleton className="h-3.5 w-1/3" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

export type { SkeletonProps, SkeletonRowsProps, SkeletonTextProps };
